import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll } from 'framer-motion';
import { ArrowRight, ChatCircleDots, EnvelopeSimple, List, X } from '@phosphor-icons/react';
import { CTA_LABEL, NAV_ITEMS, SITE } from '../data/site';
import { EASE } from './ui/Reveal';
import LinkButton from './ui/Button';

const menuVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25, ease: EASE, when: 'beforeChildren', staggerChildren: 0.05 } },
  exit: { opacity: 0, transition: { duration: 0.2, ease: EASE } }
};

const menuItemVariants = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE } },
  exit: { opacity: 0, transition: { duration: 0.15 } }
};

function Navbar() {
  const { scrollY, scrollYProgress } = useScroll();
  const reduce = useReducedMotion();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useMotionValueEvent(scrollY, 'change', (value) => {
    const next = value > 8;
    setScrolled((prev) => (prev === next ? prev : next));
  });

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <header className="fixed inset-x-0 top-0 z-40">
      <motion.div
        aria-hidden="true"
        className="bg-brand-gradient absolute inset-x-0 top-0 z-10 h-[3px] origin-left"
        style={{ scaleX: scrollYProgress }}
      />
      <div
        className={`transition-[background-color,box-shadow,border-color] duration-300 ${
          scrolled || open
            ? 'border-b border-line/80 bg-canvas/85 shadow-nav backdrop-blur-xl'
            : 'border-b border-transparent bg-canvas/0'
        }`}
      >
        <nav
          className="mx-auto flex h-16 w-full max-w-page items-center justify-between px-5 sm:h-[68px] sm:px-8"
          aria-label="주요 메뉴"
        >
          <Link to="/" className="flex items-center gap-2 transition-opacity hover:opacity-80" aria-label="PageN 홈으로">
            <img src="/logo.png" alt="" width={32} height={28} className="h-7 w-auto" />
            <span className="text-[19px] font-bold tracking-tight">PageN</span>
          </Link>

          <ul className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `relative inline-flex h-10 items-center rounded-full px-3.5 text-[15px] font-medium transition-colors duration-200 ${
                      isActive ? 'text-ink' : 'text-ink-muted hover:text-ink'
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive ? (
                        <motion.span
                          layoutId="nav-active"
                          className="absolute inset-0 rounded-full bg-ink/[0.06]"
                          transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 36 }}
                        />
                      ) : null}
                      <span className="relative">{item.label}</span>
                    </>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <LinkButton to="/contact" size="sm" className="hidden md:inline-flex">
              {CTA_LABEL}
              <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </LinkButton>
            <button
              type="button"
              onClick={() => setOpen((prev) => !prev)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition-colors hover:bg-ink/5 md:hidden"
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? '메뉴 닫기' : '메뉴 열기'}
            >
              {open ? <X size={22} weight="bold" /> : <List size={22} weight="bold" />}
            </button>
          </div>
        </nav>
      </div>

      <AnimatePresence>
        {open ? (
          <motion.div
            id="mobile-menu"
            className="fixed inset-x-0 bottom-0 top-16 z-30 overflow-y-auto bg-canvas md:hidden"
            variants={menuVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <div className="flex min-h-full flex-col px-5 pb-8 pt-4">
              <ul className="flex flex-col">
                {NAV_ITEMS.map((item) => (
                  <motion.li key={item.to} variants={menuItemVariants} className="border-b border-line">
                    <NavLink
                      to={item.to}
                      end={item.to === '/'}
                      className={({ isActive }) =>
                        `flex items-center justify-between py-4 text-[22px] font-semibold tracking-tight ${
                          isActive ? 'text-accent' : 'text-ink'
                        }`
                      }
                    >
                      {item.label}
                      <ArrowRight size={20} weight="bold" className="text-ink-faint" />
                    </NavLink>
                  </motion.li>
                ))}
              </ul>

              <motion.div variants={menuItemVariants} className="mt-8 flex flex-col gap-3">
                <LinkButton to="/contact" size="lg" className="w-full">
                  {CTA_LABEL}
                  <ArrowRight size={18} weight="bold" />
                </LinkButton>
                <div className="grid grid-cols-2 gap-3">
                  <a
                    href={`mailto:${SITE.email}`}
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-line-strong bg-surface text-[15px] font-medium text-ink"
                  >
                    <EnvelopeSimple size={18} weight="bold" />
                    이메일
                  </a>
                  <a
                    href={SITE.kakao}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-12 items-center justify-center gap-2 rounded-full border border-line-strong bg-surface text-[15px] font-medium text-ink"
                  >
                    <ChatCircleDots size={18} weight="bold" />
                    오픈채팅
                  </a>
                </div>
              </motion.div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </header>
  );
}

export default Navbar;
